import React, { useEffect, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { ImCancelCircle } from "react-icons/im";
import { MdArrowDropDown } from "react-icons/md";

function AdvanceSearch({ searchQuery = "", onSearchValue }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [nameEnabled, setNameEnabled] = useState(true);
  const [initiatedEnabled, setInitiatedEnabled] = useState(false);
  const [placeEnabled, setPlaceEnabled] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [initiatedValue, setInitiatedValue] = useState("");
  const [placeValue, setPlaceValue] = useState("");
  const [nameError, setNameError] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (typeof searchQuery === "object" && searchQuery !== null) {
      setNameValue(searchQuery.name || "");
      setInitiatedValue(searchQuery.initiatedName || "");
      setPlaceValue(searchQuery.place || "");
      if (searchQuery.initiatedName) setInitiatedEnabled(true);
      if (searchQuery.place) setPlaceEnabled(true);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeCount = [nameEnabled, initiatedEnabled, placeEnabled].filter(Boolean).length;

  // 1 field=180px, 2 fields=130px, 3 fields=90px
  const inputWidth =
    activeCount === 1 ? "w-[180px]" :
    activeCount === 2 ? "w-[130px]" :
    "w-[90px]";

const handleSearch = () => {
  const activeValue = nameEnabled
    ? nameValue
    : initiatedEnabled
    ? initiatedValue
    : placeValue;

  if (!activeValue.trim() || activeValue.trim().length < 3) {
    setNameError("Min 3 letters required");
    return;
  }
  setNameError("");
    onSearchValue({
      name: nameEnabled ? nameValue.trim().toLowerCase() : "",
      initiatedName: initiatedEnabled ? initiatedValue.trim().toLowerCase() : "",
      place: placeEnabled ? placeValue.trim().toLowerCase() : "",
    });
    setShowDropdown(false);
  };

  const handleClear = () => {
    setNameValue("");
    setInitiatedValue("");
    setPlaceValue("");
    setNameError("");
    setNameEnabled(true);
    setInitiatedEnabled(false);
    setPlaceEnabled(false);
    onSearchValue({ name: "", initiatedName: "", place: "" });
  };

  const isActive =
    nameValue.trim().length > 0 ||
    initiatedValue.trim().length > 0 ||
    placeValue.trim().length > 0;

  return (
    <div className="flex items-center gap-1" ref={dropdownRef}>

      {/* Name Input */}
      {nameEnabled && (
        <div className={`flex items-center rounded-lg border px-2 h-[34px] bg-white ${nameError ? "border-red-400" : "border-gray-300"}`}>
          <input
            type="text"
            placeholder="Name"
            className={`${inputWidth} text-xs outline-none`}
            value={nameValue}
            onChange={(e) => { setNameError(""); setNameValue(e.target.value); }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          {nameValue && (
            <ImCancelCircle
              className="ml-1 cursor-pointer text-sm text-gray-400 hover:text-gray-600"
              onClick={() => { setNameValue(""); setNameError(""); }}
            />
          )}
        </div>
      )}

      {/* Place Input */}
      {placeEnabled && (
        <div className="flex items-center rounded-lg border border-gray-300 px-2 h-[34px] bg-white">
          <input
            type="text"
            placeholder="Place"
            className={`${inputWidth} text-xs outline-none`}
            value={placeValue}
            onChange={(e) => setPlaceValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <ImCancelCircle
            className="ml-1 cursor-pointer text-sm text-gray-400 hover:text-gray-600"
            onClick={() => { setPlaceEnabled(false); setPlaceValue(""); }}
          />
        </div>
      )}

      {/* Initiated Name Input */}
      {initiatedEnabled && (
        <div className="flex items-center rounded-lg border border-gray-300 px-2 h-[34px] bg-white">
          <input
            type="text"
            placeholder="Initiated Name"
            className={`${inputWidth} text-xs outline-none`}
            value={initiatedValue}
            onChange={(e) => setInitiatedValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <ImCancelCircle
            className="ml-1 cursor-pointer text-sm text-gray-400 hover:text-gray-600"
            onClick={() => { setInitiatedEnabled(false); setInitiatedValue(""); }}
          />
        </div>
      )}

      {/* Dropdown Arrow */}
      <div className="relative">
        <div
          className="flex h-[34px] w-[28px] cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-400 hover:text-gray-600"
          onClick={() => setShowDropdown((prev) => !prev)}
          title="Select fields"
        >
          <MdArrowDropDown className="text-xl" />
        </div>

        {showDropdown && (
          <div className="absolute right-0 top-[42px] z-50 w-[180px] rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
            <div className="absolute -top-2 right-3 h-3 w-3 rotate-45 border-l border-t border-gray-200 bg-white" />
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Select Fields
            </p>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1 hover:bg-gray-50">
              <input
                type="checkbox"
                checked={nameEnabled}
                onChange={(e) => {
                  setNameEnabled(e.target.checked);
                  if (!e.target.checked) { setNameValue(""); setNameError(""); }
                }}
                className="h-4 w-4 accent-textYellow"
              />
              <span className="text-xs font-semibold text-gray-700">
                Name <span className="text-red-500">*</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1 hover:bg-gray-50">
              <input
                type="checkbox"
                checked={initiatedEnabled}
                onChange={(e) => {
                  setInitiatedEnabled(e.target.checked);
                  if (!e.target.checked) setInitiatedValue("");
                }}
                className="h-4 w-4 accent-textYellow"
              />
              <span className="text-xs text-gray-600">Initiated Name</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1 hover:bg-gray-50">
              <input
                type="checkbox"
                checked={placeEnabled}
                onChange={(e) => {
                  setPlaceEnabled(e.target.checked);
                  if (!e.target.checked) setPlaceValue("");
                }}
                className="h-4 w-4 accent-textYellow"
              />
              <span className="text-xs text-gray-600">Place</span>
            </label>
          </div>
        )}
      </div>

      {/* Search Button */}
      <div
        className={`flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-lg transition-all ${
          isActive ? "bg-textYellow text-white" : "bg-gray-500 text-white hover:bg-gray-600"
        }`}
        onClick={handleSearch}
        title="Search"
      >
        <FaSearch className="text-xs" />
      </div>

      {nameError && (
        <p className="text-[10px] text-red-500">{nameError}</p>
      )}
    </div>
  );
}

export default AdvanceSearch;